import {useEffect, useState} from 'react';

const useForm = (contextState) => {
    const [form, setForm] = useState({});
    const [dirty, setDirty] = useState(false);

    /**
     * Initialize the form state with the context state.
     * This effect runs once when the component mounts and whenever the context state changes.
     */
    useEffect(() => {
        updateForm(contextState);
    }, [contextState]);

    /**
     * Check if the form values are dirty compared to the context state.
     * A form is considered dirty if any of its values differ from the context state.
     * If the context state is undefined, it treats it as an empty string for comparison.
     * @param {object} values
     */
    const checkIfDirty = (values) => {
        const isDirty = Object.keys(values).some(key => (
            !(contextState[key] === undefined && values[key] === '') && values[key] !== contextState[key]
        ));
        setDirty(isDirty);
    }

    /**
     * Update the form state with new values.
     * @param {object} values
     */
    const updateForm = (values) => {
        const formDefaults = {};
        for (const key in values) {
            formDefaults[key] = values[key] ?? '';
        }
        setForm(formDefaults);
        checkIfDirty(formDefaults);
    }

    /**
     * Handle form input changes.
     * Updates the form state with the new value and checks if the form is dirty.
     * @param {object} event
     */
    const handleChange = (event) => {
        const newForm = {...form, [event.target.name]: event.target.value };
        setForm(newForm);
        checkIfDirty(newForm);
    }

    /**
     * Validate the form and apply styles for inputs.
     * @param {object} formEl
     * @returns {boolean} Return true if the form is valid, false otherwise.
     */
    const validateForm = (formEl) => {
        if (formEl.checkValidity() === false) {
            for (const elem of formEl) {
                elem.classList.remove("error");
                elem.classList.remove("is-invalid");
                if (!elem.validity.valid) {
                    elem.classList.add("error");
                    elem.classList.add("is-invalid");
                }
            }
            return false;
        } else {
            for (const elem of formEl) {
                elem.classList.remove("is-invalid");
            }
            return true;
        }
    }

    return {
        form,
        dirty,
        handleChange,
        validateForm,
        updateForm
    }
}

export default useForm;
